import { useMemo } from 'react';
import { BadgeCheck, BadgeX, TriangleAlert } from 'lucide-react';
import { getValidationIssues } from '@/pages/workflow/helper/functional-control-flow';
import {
  describeValidationIssue,
  summarizeValidationResult,
} from '@/pages/workflow/helper/validate-builder';
import { cn } from '@/lib/utils';

/**
 * After a run, a Validate node's outcome in words: a compact chip for the
 * node body and the error list for the result panel. Reads
 * `metadata.validation` (present in fail and collect mode alike).
 */

export function isValidationResult(result: unknown): boolean {
  return summarizeValidationResult(result, getValidationIssues) !== null;
}

export function ValidationResultChip({ result }: { result: unknown }) {
  const summary = useMemo(
    () => summarizeValidationResult(result, getValidationIssues),
    [result],
  );
  if (!summary) return null;
  if (summary.runtimeError) {
    return (
      <div
        className='mt-2 flex items-center gap-1 rounded px-2 py-1 text-xs bg-red-50 text-red-700 dark:bg-red-950/30 dark:text-red-300'
        data-testid='validate-result-chip'
      >
        <TriangleAlert className='size-3.5 shrink-0' />
        <span className='truncate'>Checks could not run</span>
      </div>
    );
  }
  const ok = summary.isValid === true;
  return (
    <div
      className={cn(
        'mt-2 flex items-center gap-1 rounded px-2 py-1 text-xs',
        ok
          ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300'
          : 'bg-red-50 text-red-700 dark:bg-red-950/30 dark:text-red-300',
      )}
      data-testid='validate-result-chip'
      title={summary.summary}
    >
      {ok ? (
        <BadgeCheck className='size-3.5 shrink-0' />
      ) : (
        <BadgeX className='size-3.5 shrink-0' />
      )}
      <span className='truncate'>{summary.summary}</span>
    </div>
  );
}

export function ValidationErrorsList({ result }: { result: unknown }) {
  const summary = useMemo(
    () => summarizeValidationResult(result, getValidationIssues),
    [result],
  );
  if (!summary) return null;
  return (
    <div className='mb-3 space-y-2' data-testid='validate-errors-list'>
      <div
        className={cn(
          'flex items-center gap-2 rounded-md px-3 py-2 text-sm',
          summary.runtimeError || summary.isValid === false
            ? 'bg-red-50 text-red-800 dark:bg-red-950/30 dark:text-red-200'
            : 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-200',
        )}
      >
        {summary.runtimeError ? (
          <TriangleAlert className='size-4 shrink-0' />
        ) : summary.isValid ? (
          <BadgeCheck className='size-4 shrink-0' />
        ) : (
          <BadgeX className='size-4 shrink-0' />
        )}
        <span>{summary.runtimeError ?? summary.summary}</span>
      </div>
      {summary.issues.length > 0 && (
        <ol className='space-y-1 text-sm'>
          {summary.issues.map((issue, index) => (
            <li
              key={`${issue.code}-${index}`}
              className='flex items-start gap-2 rounded border px-2 py-1.5'
              data-testid='validate-error-item'
            >
              <span className='text-muted-foreground text-xs w-5 shrink-0 pt-0.5'>
                {index + 1}.
              </span>
              <div className='min-w-0'>
                <div className='flex flex-wrap items-center gap-1.5'>
                  <code className='text-xs rounded bg-muted px-1 py-0.5'>
                    {issue.code}
                  </code>
                  <span className='text-[10px] uppercase tracking-wide text-muted-foreground'>
                    {issue.source === 'schema' ? 'shape' : 'rule'}
                  </span>
                </div>
                <p className='text-sm'>{describeValidationIssue(issue)}</p>
              </div>
            </li>
          ))}
        </ol>
      )}
      {summary.errorsTruncated && (
        <p className='text-xs text-muted-foreground'>
          Only the first {summary.issues.length} errors are kept.
        </p>
      )}
    </div>
  );
}
